<script setup lang="ts">
import { computed, watch, ref } from 'vue'
import { useStore } from 'vuex'
import chip from '@/assets/icons/chip.svg'
import GemsHistoryItem from '@/components/Gems/GemsHistoryItem.vue'
import { useAnimatedNumber } from '@/composables/useAnimatedNumber'

const store = useStore()

type TransactionItem = {
  amount: string
  date_created: string
  name: string
  description: string
}

const props = defineProps<{
  items: TransactionItem[]
  isLoading: boolean
}>()

const lastItemIndex = computed(() => props.items.length - 1)
const creditBalance = computed(() => store.getters.user_info.credit_balance)

const { animatedValue: animatedCreditBalance } = useAnimatedNumber(creditBalance, 1000)
</script>

<template>
  <div
    class="bottom_container d-flex"
    style="gap: var(--app-gap-6)"
  >
    <div
      v-if="props.items && props.items.length"
      class="transaction_box"
      style="flex: 2 0 0px"
      :class="{ gw_loading: true }"
    >
      <div
        class="text-h7 d-flex justify-space-between"
        style="margin: 1rem"
      >
        <span class="history">{{ $t('user_settings.credits_history') }}</span>
        <span
          style="
            display: flex;
            align-items: center;
            gap: 0.5rem;
            color: var(--palette-text-secondary);
          "
        >
          Your wallet:
          <component
            :is="chip"
            :style="{ color: 'var(--palette-primary-main)', width: '18px', height: '18px' }"
          />
          <span
            style="
              font: var(--font-subtitle1);
              color: var(--palette-primary-main) !important;
              font-size: 20px;
            "
          >
            {{ animatedCreditBalance.toLocaleString() }}
          </span>
        </span>
      </div>
      <transition-group
        name="list"
        tag="div"
        class="transaction_box_content d-flex flex-column large-scroll-track"
      >
        <GemsHistoryItem
          v-for="(item, index) in props.items"
          :key="item.date_created + '-' + item.amount"
          :item="item"
          :class="[{ 'new-item': index === lastItemIndex }, { gw_loading_text: props.isLoading }]"
        />
      </transition-group>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.bottom_container {
  min-height: 30rem;
  margin-top: 2rem;
  border-radius: var(--4, 12px);
  gap: 6rem;
  background-color: var(--clr-black-0);
}

@media (max-width: 1023px) {
  .bottom_container {
    flex-direction: column;
  }
}

.transaction_box {
  display: flex;
  flex-direction: column;
  border-radius: 1.875rem;

  &_content {
    gap: 0.8rem;
    max-height: 30rem;
    overflow-y: scroll;
    overflow-x: hidden;
    margin: 0 0 1rem 0;
  }

  .history {
    font: var(--font-subtitle1);
    font-size: 20px;
  }
}

@media (max-width: 620px) {
  .transaction_box {
    border-radius: 12px;
    .history {
      font-size: 16px;
    }
  }
}

.list-enter-active {
  transition: all 0.5s ease;
}
.list-enter-from {
  opacity: 0;
  transform: translateY(20px);
}
.list-enter-to {
  opacity: 1;
  transform: translateY(0);
}

.new-item {
  animation: fade-in-up 0.5s ease forwards;
}

@keyframes fade-in-up {
  0% {
    opacity: 0;
    transform: translateY(30px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
